import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Input } from 'antd';

import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import TileButton from 'components/atoms/TileButton';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/CreateEditProjectContainer';
import AttributeSelectField from 'components/molecules/TradePromoOptimization/BulkExport/AttributeSelectField';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import type {
    AttributeOption,
    FilterDimension,
    ReportSetupConfig,
    ReportSetupFieldType,
    ReportSetupSection,
    ReportSetupSubmitPayload,
    ReportSetupTab,
    ResolvedField,
} from 'types/tpo/reportSetup';
import { sendNotification, transformFiltersV2 } from 'utils/helperMethods';
import {
    buildFormStateFromReport,
    isValueFilled,
    sectionColumnsClass,
    toAttributeFieldValue,
} from 'utils/tpoReportSetupHelpers';

// `is_tpm_plan` is only enabled when the Operational plan version is picked.
// This must match the stored plan_version value (attributeExId), not the UI label.
const OPERATIONAL_PLAN_VERSION = 'operational';

const REPORT_NAME_SECTION_KEY = 'report_name';

type Props = {
    open: boolean;
    onCancel: () => void;
    onSubmit: (payload: ReportSetupSubmitPayload) => void;
    config?: ReportSetupConfig;
    loading?: boolean;
    mode?: 'create' | 'view' | 'edit';
    reportId?: number | string | null;
};

const ReportSetupModal: FC<Props> = ({
    open,
    onCancel,
    onSubmit,
    config,
    loading = false,
    mode = 'create',
    reportId = null,
}) => {
    const tabs = config?.tabs ?? [];
    const firstTabKey = tabs[0]?.key ?? '';
    const isViewMode = mode === 'view';
    const isEditMode = mode === 'edit';
    const isExistingReport = isViewMode || isEditMode;
    const lockTabs = isViewMode || isEditMode;

    const hasAnyDimTypeSection = useMemo(() => tabs.some((t) => t.sections.some((s) => !!s.dimType)), [tabs]);

    const {
        data: filterDimensions,
        loading: dimsLoading,
        error: dimsError,
    } = useCommonApiHandler({
        method: 'get',
        url: tradePromoOptimizationUrls.getReportsFilterDimensionsUrl,
        queryKey: ['tpo-reports-filter-dimensions'],
        parserFunction: (payload: any) => payload?.dimensions ?? [],
        queryConfig: { enabled: open && hasAnyDimTypeSection },
    });

    const {
        data: reportDetails,
        loading: detailsLoading,
        error: detailsError,
    } = useCommonApiHandler({
        method: 'get',
        url: reportId != null ? tradePromoOptimizationUrls.getReportDetailsUrl(reportId) : '',
        queryKey: ['tpo-reports-details', reportId],
        parserFunction: (payload: any) => payload,
        queryConfig: { enabled: open && isExistingReport && reportId != null },
    });

    const [activeTabKey, setActiveTabKey] = useState<string>(firstTabKey);
    /** Per-tab form values so switching tabs doesn't lose user input. */
    const [formValuesByTab, setFormValuesByTab] = useState<Record<string, Record<string, any>>>({});
    // chip-label cache for hydrated reports — lets saved chips render before any cascade fetch
    const [presetOptionsByFieldKey, setPresetOptionsByFieldKey] = useState<Record<string, AttributeOption[]>>({});
    const [selectedFilter, setSelectedFilter] = useState<ResolvedField | null>(null);
    const { loading: saving, mutate: saveReport } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.getReportsUpsertUrl,
        method: 'post',
    });

    const handleFieldOpen = useCallback((field: ResolvedField) => {
        setSelectedFilter(field);
    }, []);

    const getSectionFields = useCallback(
        (section: ReportSetupSection, tabKey: string): ResolvedField[] => {
            const allFilterDimensions = (filterDimensions as FilterDimension[] | null) ?? [];

            if (!section.dimType) {
                return (section.fields ?? []).map((field) => {
                    if (!field.optionsFromDimType) return field;
                    const dimensionOptions = allFilterDimensions
                        .filter(
                            (dimension) =>
                                dimension.dim_type === field.optionsFromDimType &&
                                (dimension.available_for ?? []).includes(tabKey) &&
                                (field.optionsIsFilter == null || dimension.is_filter === field.optionsIsFilter),
                        )
                        .map((dimension) => ({ label: dimension.ui_label, value: dimension.db_col_name }));
                    return { ...field, options: dimensionOptions };
                });
            }

            const matchingDimensions = allFilterDimensions.filter(
                (dimension) =>
                    dimension.dim_type === section.dimType &&
                    dimension.is_filter === 1 &&
                    (dimension.available_for ?? []).includes(tabKey),
            );
            return matchingDimensions.map((dimension): ResolvedField => {
                const fieldOverride = section.fieldOverrides?.[dimension.db_col_name] ?? {};

                const selectionLimit = dimension.selection_limit;
                const isSelectionLimitSpecified = selectionLimit === null || typeof selectionLimit === 'number';
                const derivedFieldType: ReportSetupFieldType | undefined =
                    selectionLimit === 1 ? 'select' : isSelectionLimitSpecified ? 'multi-select' : undefined;
                const derivedMaxSelections: number | undefined =
                    typeof selectionLimit === 'number' && selectionLimit > 1 ? selectionLimit : undefined;

                return {
                    key: dimension.db_col_name,
                    label: dimension.ui_label,
                    type: fieldOverride.type ?? derivedFieldType ?? section.fieldType ?? 'select',
                    placeholder: fieldOverride.placeholder ?? 'Select',
                    required: fieldOverride.required ?? !!dimension.is_required,
                    dependsOn: fieldOverride.dependsOn,
                    options: fieldOverride.options ?? [],
                    defaultValue: fieldOverride.defaultValue,
                    selectAll: fieldOverride.selectAll,
                    maxSelections: fieldOverride.maxSelections ?? derivedMaxSelections,
                    dimension: dimension.dim_type,
                    attributeConfigId: dimension.id,
                };
            });
        },
        [filterDimensions],
    );

    const getTabFields = useCallback(
        (tab: ReportSetupTab | undefined): ResolvedField[] =>
            tab?.sections?.flatMap((section) => getSectionFields(section, tab?.key ?? '')) ?? [],
        [getSectionFields],
    );

    // reset state on modal open; hydration effect below fills view/edit modes
    // filterDimensions deliberately omitted from deps — late API response shouldn't wipe user input
    useEffect(() => {
        if (!open) return;
        setActiveTabKey(firstTabKey);
        setSelectedFilter(null);
        setPresetOptionsByFieldKey({});

        if (isExistingReport) {
            setFormValuesByTab({});
            return;
        }

        const defaultValuesByTab: Record<string, Record<string, any>> = {};
        for (const tab of tabs) {
            const tabDefaults: Record<string, any> = {};
            for (const section of tab.sections) {
                for (const field of getSectionFields(section, tab.key)) {
                    if (field.defaultValue !== undefined) tabDefaults[field.key] = field.defaultValue;
                }
            }
            if (Object.keys(tabDefaults).length) defaultValuesByTab[tab.key] = tabDefaults;
        }
        setFormValuesByTab(defaultValuesByTab);
    }, [open, firstTabKey, isExistingReport]);

    useEffect(() => {
        if (!open || !isExistingReport || !reportDetails) return;
        const next = buildFormStateFromReport(reportDetails, firstTabKey);
        setActiveTabKey(next.activeTabKey);
        setFormValuesByTab(next.formValuesByTab);
        setPresetOptionsByFieldKey(next.presetOptionsByFieldKey);
    }, [open, isExistingReport, reportDetails, firstTabKey]);

    const activeTab = useMemo(() => tabs.find((t) => t.key === activeTabKey) ?? tabs[0], [tabs, activeTabKey]);
    const formValues = formValuesByTab[activeTabKey] ?? {};

    const setFieldValue = (fieldKey: string, value: any) => {
        const allFields = getTabFields(activeTab);

        setFormValuesByTab((prevFormValuesByTab) => {
            const tabValues = { ...(prevFormValuesByTab[activeTabKey] ?? {}), [fieldKey]: value };

            const dependentFieldKeys = allFields
                .filter((field) => field.dependsOn?.includes(fieldKey))
                .map((field) => field.key);
            dependentFieldKeys.forEach((dependentKey) => {
                delete tabValues[dependentKey];
            });

            return { ...prevFormValuesByTab, [activeTabKey]: tabValues };
        });
    };

    const isApplyDisabled = useMemo(() => {
        const fields = getTabFields(activeTab);
        return fields.some((field) => field.required && !isValueFilled(formValues[field.key]));
    }, [activeTab, formValues, getTabFields]);

    const isFieldDependencyMet = (field: ResolvedField) =>
        !field.dependsOn?.length || field.dependsOn.every((depKey) => isValueFilled(formValues[depKey]));

    const buildFiltersPayload = useCallback(
        (skipFieldKey: string) => {
            const filterConfig: { value: string; dimension: string }[] = [];
            const attributeConfigIdByKey: Record<string, number | undefined> = {};

            for (const section of activeTab?.sections ?? []) {
                if (!section.dimType) continue;
                for (const field of getSectionFields(section, activeTabKey)) {
                    filterConfig.push({ value: field.key, dimension: section.dimType });
                    attributeConfigIdByKey[field.key] = field.attributeConfigId;
                }
            }

            const applyFilterObject: Record<string, { inclusion: string[]; exclusion: string[] }> = {};
            for (const filterEntry of filterConfig) {
                // "Select All" (all=true) -> send exclusion of removed items; otherwise inclusion of picked items.
                const fieldValue = toAttributeFieldValue(formValues[filterEntry.value]);
                const valuesAsStrings = fieldValue.values.map(String);
                applyFilterObject[filterEntry.value] = fieldValue.all
                    ? { inclusion: [], exclusion: valuesAsStrings }
                    : { inclusion: valuesAsStrings, exclusion: [] };
            }

            const filtersByDimension = transformFiltersV2(applyFilterObject, filterConfig, skipFieldKey) as Record<
                string,
                { inclusion: any[]; exclusion: any[] }
            >;

            for (const dimensionKey of Object.keys(filtersByDimension)) {
                for (const bucket of ['inclusion', 'exclusion'] as const) {
                    filtersByDimension[dimensionKey][bucket] = filtersByDimension[dimensionKey][bucket].map(
                        (filterItem: any) => ({
                            ...filterItem,
                            attributeConfigId: attributeConfigIdByKey[filterItem.attributeType],
                        }),
                    );
                }
            }
            return filtersByDimension;
        },
        [activeTab, activeTabKey, formValues, getSectionFields],
    );

    const renderField = (field: ResolvedField) => {
        // is_tpm_plan is enabled when "operational" is among the selected plan versions (multi-select).
        const isTpmPlanLocked =
            field.key === 'is_tpm_plan' &&
            !toAttributeFieldValue(formValues.plan_version).values.some(
                (v) => String(v).toLowerCase() === OPERATIONAL_PLAN_VERSION,
            );
        const disabled = isViewMode || !isFieldDependencyMet(field) || isTpmPlanLocked || loading;
        const value = formValues[field.key];
        const isAttributeField = field.attributeConfigId != null && !!field.dimension;
        const fieldPresetOptions = presetOptionsByFieldKey[field.key];

        switch (field.type) {
            case 'select':
                if (isAttributeField) {
                    return (
                        <AttributeSelectField
                            field={field}
                            value={value}
                            disabled={disabled}
                            multi={false}
                            onChange={(val) => setFieldValue(field.key, val)}
                            buildFilters={buildFiltersPayload}
                            selectedFilter={selectedFilter}
                            onFieldOpen={handleFieldOpen}
                            viewMode={isViewMode}
                            presetOptions={fieldPresetOptions}
                        />
                    );
                }
                if (field.optionsFromDimType && dimsLoading) return <Loader />;
                return (
                    <SelectWithInfiniteScroller
                        hasMore={false}
                        className="w-full"
                        style={{ width: '100%' }}
                        placeholder={field.placeholder ?? 'Select'}
                        value={value ?? undefined}
                        options={field.options ?? []}
                        showArrow
                        showSearch={!isViewMode}
                        allowClear={!isViewMode}
                        disabled={isViewMode ? false : disabled}
                        onChange={(val: any) => {
                            if (isViewMode) return;
                            setFieldValue(field.key, val);
                        }}
                        filterOption={(input: string, option: any) =>
                            String(option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    />
                );
            case 'multi-select': {
                if (isAttributeField) {
                    return (
                        <AttributeSelectField
                            field={field}
                            value={value}
                            disabled={disabled}
                            multi
                            onChange={(val) => setFieldValue(field.key, val)}
                            buildFilters={buildFiltersPayload}
                            selectAllConfig={{ enabled: !!field.selectAll }}
                            selectedFilter={selectedFilter}
                            onFieldOpen={handleFieldOpen}
                            viewMode={isViewMode}
                            presetOptions={fieldPresetOptions}
                        />
                    );
                }
                if (field.optionsFromDimType && dimsLoading) return <Loader />;
                const opts = field.options ?? [];
                const allValues = opts.map((o) => o.value);
                const cap = field.maxSelections ?? allValues.length;
                const selectAllTarget = allValues.slice(0, cap);
                const isAllSelected =
                    Array.isArray(value) && selectAllTarget.length > 0 && value.length === selectAllTarget.length;

                return (
                    <SelectWithInfiniteScroller
                        hasMore={false}
                        className="w-full"
                        style={{ width: '100%' }}
                        mode="multiple"
                        maxTagCount="responsive"
                        placeholder={field.placeholder ?? 'Select'}
                        value={value ?? []}
                        options={opts}
                        showArrow
                        showSearch={!isViewMode}
                        allowClear={!isViewMode}
                        disabled={isViewMode ? false : disabled}
                        removeIcon={isViewMode ? null : undefined}
                        dropdownRender={
                            !isViewMode && field.selectAll
                                ? (menu) => (
                                      <>
                                          {selectAllTarget.length > 0 && (
                                              <div
                                                  className="px-3 py-2 cursor-pointer border-b border-backgroundGreyAlt6 text-violetAlt font-xs"
                                                  // Prevent the dropdown from closing on click.
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() =>
                                                      setFieldValue(field.key, isAllSelected ? [] : selectAllTarget)
                                                  }
                                              >
                                                  {isAllSelected ? 'Deselect All' : 'Select All'}
                                              </div>
                                          )}
                                          {menu}
                                      </>
                                  )
                                : undefined
                        }
                        onChange={(val: any) => {
                            if (isViewMode) return;
                            if (field.maxSelections && Array.isArray(val) && val.length > field.maxSelections) {
                                sendNotification(
                                    'info',
                                    `You can select up to ${field.maxSelections} ${field.label.toLowerCase()}.`,
                                );
                                setFieldValue(field.key, val.slice(0, field.maxSelections));
                                return;
                            }
                            setFieldValue(field.key, val);
                        }}
                        filterOption={(input: string, option: any) =>
                            String(option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    />
                );
            }
            case 'text':
                return (
                    <Input
                        style={{ borderRadius: 6, fontSize: 13 }}
                        placeholder={field.placeholder ?? ''}
                        value={value ?? ''}
                        disabled={isViewMode ? false : disabled}
                        readOnly={isViewMode}
                        onChange={(e) => {
                            if (isViewMode) return;
                            setFieldValue(field.key, e.target.value);
                        }}
                    />
                );
            case 'checkbox':
                return (
                    <Checkbox
                        checked={!!value}
                        disabled={isViewMode ? false : disabled}
                        onChange={(e) => {
                            if (isViewMode) return;
                            setFieldValue(field.key, e.target.checked);
                        }}
                    >
                        <span className="font-xs">{field.label}</span>
                    </Checkbox>
                );
            default:
                return null;
        }
    };

    const renderSectionBody = (section: ReportSetupSection, innerColsClass: string) => {
        if (section.dimType) {
            if (dimsLoading) {
                return (
                    <div className="flex justify-center py-4">
                        <Loader />
                    </div>
                );
            }
            if (dimsError) {
                return <ErrorBox msg={typeof dimsError === 'string' ? dimsError : 'Failed to load dimensions'} />;
            }
        }

        const fields = getSectionFields(section, activeTabKey);
        if (!fields.length) {
            return <div className="text-fontGreyAlt font-xs py-4 text-center">No fields configured.</div>;
        }

        return (
            <div className={`grid ${innerColsClass} gap-4`}>
                {fields.map((field) => {
                    if (field.type === 'checkbox') {
                        return (
                            <div key={field.key} className="col-span-full flex items-center">
                                {renderField(field)}
                            </div>
                        );
                    }
                    return (
                        <div key={field.key} className="flex flex-col gap-1">
                            <p className="font-xs m-0 text-fontGreyAlt">
                                {field.label}
                                {field.required && <span className="text-redAlt ml-1">*</span>}:
                            </p>
                            {renderField(field)}
                        </div>
                    );
                })}
            </div>
        );
    };

    const handleApply = () => {
        if (!activeTabKey || saving || isApplyDisabled) return;

        const activeTabConfig = tabs.find((t) => t.key === activeTabKey);
        const tabDefaultPayloadValues = activeTabConfig?.defaultPayloadValues ?? {};

        const body: Record<string, any> = {
            ...tabDefaultPayloadValues,
            name: formValues.report_name,
            report_type: activeTabKey,
            // plans flow only through filters.dim10 inclusion/exclusion (empty when none selected)
            filters: buildFiltersPayload(''),
        };
        if (formValues.customer_hierarchy != null) {
            body.customer_hierarchy = formValues.customer_hierarchy;
        }
        if (formValues.product_hierarchy != null) {
            body.product_hierarchy = formValues.product_hierarchy;
        }
        if (formValues.promotion_granularity != null) {
            body.promotion_granularity = formValues.promotion_granularity;
        }
        if (formValues.enable_plan_level_data != null) {
            body.is_plan_level = !!formValues.enable_plan_level_data;
        }
        if (isEditMode && reportId != null) {
            body.report_id = reportId;
        }

        saveReport(body, {
            onSuccess: (res) => {
                sendNotification(
                    'success',
                    isEditMode ? 'Report updated successfully.' : 'Report queued successfully.',
                );
                onSubmit({
                    tab: activeTabKey,
                    values: formValues,
                    saved: res?.data,
                });
            },
            onError: (e: any) => {
                sendNotification(
                    'error',
                    e?.response?.data?.message ??
                        (isEditMode
                            ? 'Failed to update report. Please try again.'
                            : 'Failed to create report. Please try again.'),
                );
            },
        });
    };

    const modalTitleText = isViewMode ? 'Report Details' : isEditMode ? 'Edit Report' : config?.title ?? 'Report Setup';
    const activeTabLabel = tabs.find((t) => t.key === activeTabKey)?.label ?? activeTabKey ?? '';

    const titleNode = (
        <div className="flex items-center gap-3">
            <span className="font-semibold text-base">{modalTitleText}</span>
            {tabs.length > 0 &&
                (lockTabs
                    ? activeTabLabel && (
                          <span className="px-3 py-1 rounded bg-backgroundGreyAlt6 text-blackAlt font-xs">
                              {activeTabLabel}
                          </span>
                      )
                    : // Only show the switcher when there's more than one tab to switch between
                      // (e.g. Bulk Export's Plan/Promo). Single-tab variants (Plan Roll Up) skip it.
                      tabs.length > 1 && (
                          <TileButton
                              style={{ width: 'min-content' }}
                              data={tabs.map((t) => ({ key: t.key, label: t.label }))}
                              activeKey={activeTabKey}
                              onClick={(item: { key: string }) => setActiveTabKey(item.key)}
                          />
                      ))}
        </div>
    );

    const renderModalBody = () => {
        const bodyStyle = { minHeight: 480 };

        if (isExistingReport && detailsLoading) {
            return (
                <div className="flex justify-center items-center" style={bodyStyle}>
                    <Loader />
                </div>
            );
        }
        if (isExistingReport && detailsError) {
            return (
                <div className="flex justify-center items-center py-6" style={bodyStyle}>
                    <ErrorBox
                        msg={typeof detailsError === 'string' ? detailsError : 'Failed to load report details.'}
                    />
                </div>
            );
        }
        const allSections = activeTab?.sections ?? [];
        // Report Name is pulled out of the grid and shown as an inline label : input row up top.
        const reportNameSection = allSections.find((section) => section.key === REPORT_NAME_SECTION_KEY);
        const gridSections = allSections.filter((section) => section.key !== REPORT_NAME_SECTION_KEY);
        const reportNameField = reportNameSection ? getSectionFields(reportNameSection, activeTabKey)[0] : undefined;

        return (
            <div style={bodyStyle}>
                {reportNameField && (
                    <div className="flex items-center gap-3 mb-4 mt-2">
                        <p className="font-xs m-0 text-fontGreyAlt whitespace-nowrap">
                            {reportNameField.required && <span className="text-redAlt mr-1">*</span>}
                            {reportNameField.label}:
                        </p>
                        <div style={{ width: 360 }}>{renderField(reportNameField)}</div>
                    </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                    {gridSections.map((section) => {
                        const isFullWidth = (section.width ?? 'full') === 'full';
                        const innerColsClass = sectionColumnsClass[section.columns ?? 2] ?? 'grid-cols-2';
                        return (
                            <div
                                key={section.key}
                                className={`rounded border border-backgroundGreyAlt6 p-4 bg-white ${
                                    isFullWidth ? 'col-span-2' : ''
                                }`}
                            >
                                <h5 className="font-xs font-semibold m-0 mb-3 text-blackAlt">{section.title}</h5>
                                {renderSectionBody(section, innerColsClass)}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <CustomModal
            open={open}
            onCancel={saving ? undefined : onCancel}
            onOk={isViewMode ? undefined : handleApply}
            title={titleNode}
            okText={saving ? 'Saving...' : 'Apply'}
            cancelText={isViewMode ? 'Close' : undefined}
            cancelButtonProps={{ disabled: loading || saving, style: { borderRadius: 6 } }}
            okButtonProps={{
                disabled: isApplyDisabled || loading || saving,
                loading: saving,
                // Hide Apply entirely in view mode — read-only experience, no save action.
                style: isViewMode ? { display: 'none' } : { borderRadius: 6 },
            }}
            width={1024}
            centered
            destroyOnClose
            maskClosable={false}
        >
            {renderModalBody()}
        </CustomModal>
    );
};

export default ReportSetupModal;
